import { Card, CardContent } from "@component/card";
import TextEditor from "@component/rich-editor/v2/text-editor";
import { TRAINING_TYPE_LABEL } from "@type/training";
import type { TrainingType } from "@type/training";

interface Props {
    employerName: string;
    campaignName: string;
    trainingType: TrainingType | null;
    personaName: string;
    sampleEmployeeName?: string;
    sampleEmployeeEmail?: string;
    simplified?: boolean;
    /**
     * Editable mode (simplified preview only): when true, the Subject becomes
     * a plain input and the entire body — greeting, main paragraph, and the
     * "unique link / 7 days" footer — collapses into a single rich text
     * editor whose value is HTML. The non-editable preview still renders
     * those three sections separately.
     */
    editable?: boolean;
    /** Controlled value for the Subject line (plain text). */
    subjectValue?: string;
    onSubjectChange?: (next: string) => void;
    /** Controlled value for the body (HTML when editable, plain when not). */
    bodyValue?: string;
    onBodyChange?: (next: string) => void;
}

function TemplateVar({ label }: { label: string }) {
    return (
        <code className="bg-blue-50 text-blue-600 rounded px-1 py-0.5 font-mono text-xs not-italic">
            {`{{ ${label} }}`}
        </code>
    );
}

export function EmailPreview({
    employerName,
    campaignName,
    trainingType,
    personaName,
    sampleEmployeeName = "Alice",
    sampleEmployeeEmail,
    simplified = false,
    editable = false,
    subjectValue,
    onSubjectChange,
    bodyValue,
    onBodyChange,
}: Props) {
    const typeLabel = trainingType ? TRAINING_TYPE_LABEL[trainingType] : "Training";
    const defaultSubject = `You've been invited to complete a ${typeLabel} roleplay by ${employerName || "your company"}`;
    // Default HTML body — used as the initial value of the rich-text editor in
    // editable mode AND as the plain rendered content in read-only mode.
    // Each sentence sits on its own paragraph block so the message reads as
    // a clear, scannable set of beats rather than a dense wall of text.
    const employer = employerName || "Your company";
    // Blank `<p></p>` spacer blocks sit between every paragraph so the editor —
    // and the resulting HTML — carries a true empty line between beats, matching
    // the cadence of the delivered email. (A CSS block-margin alone does not
    // render reliably inside the draft-js editor, so the spacing is baked into
    // the content itself.)
    const defaultBodyHtml = [
        `<p>Hi {{ firstName }},</p>`,
        `<p></p>`,
        `<p><strong>${employer}</strong> has assigned you an AI roleplay training session as part of the <strong>${campaignName}</strong> campaign.</p>`,
        `<p></p>`,
        `<p>It takes around {{ maxDuration }} to complete, and you'll receive a score and personalised feedback straight after.</p>`,
        `<p></p>`,
        `<p><em>Your invite link is unique to you and will expire after 7 days.</em></p>`,
        `<p></p>`,
        `<p><em>If you have any questions, reach out to your HR team.</em></p>`,
    ].join("");
    const subject = subjectValue ?? defaultSubject;

    if (simplified) {
        return (
            <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                <div className="px-5 py-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                        Subject
                    </div>
                    {editable ? (
                        <input
                            type="text"
                            value={subject}
                            onChange={(e) => onSubjectChange?.(e.target.value)}
                            className="w-full text-sm text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-200 focus:border-blue-500 focus:outline-none focus:ring-0 px-0 py-1 transition-colors"
                        />
                    ) : (
                        <div className="text-sm text-gray-900">{subject}</div>
                    )}
                </div>
                <div className="px-5 py-5">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                        Body
                    </div>
                    {editable ? (
                        // Paragraph spacing is carried by empty `<p></p>` spacer
                        // blocks baked into defaultBodyHtml (draft-js block margins
                        // don't render reliably here), so the blocks themselves keep
                        // zero margin to avoid doubling the gap.
                        <div className="email-body-editor">
                            <style dangerouslySetInnerHTML={{
                                __html: `
                                    .email-body-editor .public-DraftStyleDefault-block {
                                        margin: 0;
                                    }
                                `,
                            }} />
                            {/* Key on the default-body so swapping back to defaults
                                (Reset button → bodyValue=undefined) or changing the
                                campaign/employer name actually re-seeds the editor.
                                draft-js otherwise holds its own internal state. */}
                            <TextEditor
                                key={`${bodyValue === undefined ? "default" : "custom"}:${defaultBodyHtml.length}`}
                                defaultValue={bodyValue ?? defaultBodyHtml}
                                onChange={(html) => onBodyChange?.(html)}
                                placeholder="Write the invitation message..."
                                minHeight={220}
                                initialHeight={220}
                                maxHeight={400}
                            />
                        </div>
                    ) : bodyValue ? (
                        // Non-editable but a custom body has been set elsewhere
                        // — render it as HTML (typography-styled paragraph).
                        <div
                            className="text-sm text-gray-700 leading-relaxed space-y-4 [&>p]:m-0"
                            dangerouslySetInnerHTML={{ __html: bodyValue }}
                        />
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-900">
                                Hi <TemplateVar label="firstName" />,
                            </p>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                <span className="font-medium text-gray-900">{employer}</span>{" "}
                                has assigned you an AI roleplay training session as part of the{" "}
                                <span className="font-medium text-gray-900">{campaignName}</span> campaign.
                                It takes around <TemplateVar label="maxDuration" /> to complete, and you'll
                                receive a score and personalised feedback straight after.
                            </p>
                            <p className="text-xs text-gray-400 leading-relaxed">
                                Your invite link is unique to you and will expire after 7 days. If you
                                have any questions, reach out to your HR team.
                            </p>
                        </div>
                    )}
                    {/* Preview-only reference for the Start Roleplay link + its
                        copy-link fallback, shown below the message body. This is
                        NOT the delivered CTA: the backend builds the real email
                        (get_training_invite_email_body) with its own clickable
                        Start Roleplay button — here we just surface the
                        {{ sessionUrl }} variable so the trainer can see where the
                        per-recipient link goes. Both are non-editable. */}
                    <div className="mt-5">
                        <div className="text-sm text-gray-700">
                            <span className="font-semibold text-gray-900">Start Roleplay:</span>{" "}
                            <TemplateVar label="sessionUrl" />
                        </div>
                        <div className="text-xs text-gray-500 leading-relaxed mt-3">
                            Or copy this link into your browser:{" "}
                            <TemplateVar label="sessionUrl" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Card className="overflow-hidden">
            <div className="bg-gray-100 border-b border-gray-200 px-4 py-2.5 flex items-center gap-2 text-xs">
                <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
                </div>
                <div className="text-gray-500 ml-2">Email preview</div>
            </div>

            <div className="px-5 py-4 border-b border-gray-200 text-sm space-y-1.5">
                <Row label="From" value={`${employerName} via ourteam <invites@ourteam.ai>`} />
                <Row
                    label="To"
                    value={
                        sampleEmployeeEmail ||
                        `${sampleEmployeeName.toLowerCase()}@your-company.com`
                    }
                />
                <Row label="Subject" value={subject} bold />
            </div>

            <CardContent className="p-6 bg-white">
                <div className="max-w-md mx-auto">
                    <div className="text-center mb-5">
                        <div className="inline-block bg-blue-600 text-white text-sm font-bold px-3 py-1 rounded">
                            ourteam · Training
                        </div>
                    </div>

                    <h2 className="text-lg font-semibold text-gray-900 mb-3">
                        Hi {sampleEmployeeName},
                    </h2>

                    <p className="text-sm text-gray-700 leading-relaxed mb-3">
                        <span className="font-medium">{employerName}</span> has assigned you an
                        AI roleplay training session. It takes around <TemplateVar label="maxDuration" /> to
                        complete and you'll receive a score and personalised feedback straight after.
                    </p>

                    <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                        <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">
                            Your scenario
                        </div>
                        <div className="text-sm font-semibold text-gray-900">{personaName}</div>
                        <div className="text-xs text-gray-500 mt-1">
                            {typeLabel} · {campaignName}
                        </div>
                    </div>

                    <div className="text-center mb-5 text-sm text-gray-700">
                        <span className="font-semibold text-gray-900">Start Roleplay:</span>{" "}
                        <TemplateVar label="sessionUrl" />
                    </div>

                    <p className="text-xs text-gray-500 leading-relaxed">
                        Your invite link is unique to you and will expire after 7 days. If you
                        have any questions, reach out to your HR team.
                    </p>

                    <div className="border-t border-gray-100 mt-6 pt-4 text-center text-[11px] text-gray-400">
                        Powered by ourteam · You received this because you're part of{" "}
                        {employerName}'s training programme.
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function Row({
    label,
    value,
    bold,
}: {
    label: string;
    value: string;
    bold?: boolean;
}) {
    return (
        <div className="flex gap-2">
            <div className="text-gray-500 w-14 flex-shrink-0">{label}:</div>
            <div className={bold ? "font-semibold text-gray-900" : "text-gray-700"}>
                {value}
            </div>
        </div>
    );
}
